import { useBuyerAuth } from '@/store/BuyerAuthContext';
import LoginPromptDialog from '@/components/buyer/LoginPromptDialog';
// 首页主视觉使用买家端 UI 原图，保留接口文案轮播与询价入口。
import { Box, Typography, Button, TextField, InputAdornment } from '@mui/material';
import { ArrowForward, Search } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bannerApi } from '@/api/bannerApi';
import { usePrefs } from '@/store/BuyerPrefsContext';

import { tr } from '@/i18n';
interface HeroItem {
  title: string;
  titleEn?: string;
  imageUrl?: string;
}

export default function HeroBanner() {
  const { t, lang } = usePrefs();
  const navigate = useNavigate();
  const { isAuthenticated } = useBuyerAuth();
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [banners, setBanners] = useState<HeroItem[]>([]);
  const [index, setIndex] = useState(0);
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    bannerApi
      .list()
      .then((list) =>
        setBanners((list ?? []).map((b) => ({ title: b.title, titleEn: b.titleEn, imageUrl: b.imageUrl }))),
      )
      .catch(() => setBanners([]));
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % banners.length), 4500);
    return () => clearInterval(timer);
  }, [banners.length]);

  const current = banners[index];
  // 非中文界面优先英文标题（韩文界面暂无韩文运营标题，用英文），空则回退中文
  const bannerTitle = current ? (lang !== 'zh' && current.titleEn ? current.titleEn : current.title) : '';

  const startInquiry = () => {
    if (!isAuthenticated) { setLoginPromptOpen(true); return; }
    const initialPrompt = prompt.trim();
    navigate('/buyer/experience', initialPrompt ? { state: { initialPrompt } } : undefined);
  };

  return (
    <>
    <Box
      className="buyer-hero"
      sx={{
        borderRadius: { xs: '14px', md: '18px' },
        overflow: 'hidden',
        position: 'relative',
        minHeight: { xs: 'auto', sm: 260, md: 340 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 2.5, md: 5 },
        background: 'transparent',
      }}
    >
      <Box className="buyer-hero-panel">
      <Box className="buyer-hero-copy">
        <Typography className="buyer-hero-eyebrow">{t('hero.label')}</Typography>
        <Typography component="h1" className="buyer-hero-title">
          {bannerTitle || <>{t('hero.title1')}<br />{t('hero.title2')}</>}
        </Typography>
        {/* 只在「· 」之后折行：每段内部用不换行空格，分隔点留在行尾 */}
        <Typography className="buyer-hero-description">{t('hero.desc').split(' · ').map((part) => part.replace(/ /g, '\u00a0')).join('\u00a0· ')}</Typography>
      </Box>
      <Box className="buyer-hero-quote">
        <Box sx={{ display: 'flex', width: { xs: '100%', md: 'min(100%, 850px)' } }}>
          <TextField
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !(e as unknown as { nativeEvent?: { isComposing?: boolean } }).nativeEvent?.isComposing) {
                e.preventDefault();
                startInquiry();
              }
            }}
            className="buyer-quote-input"
            placeholder={tr('hero.tellUsWhatYouNeed')}
            sx={{
              width: 'min(100%, 680px)',
              bgcolor: 'rgba(255,255,255,.72)', backdropFilter: 'blur(16px)', borderRadius: '999px',
              '& .MuiOutlinedInput-root': { borderRadius: '999px', minHeight: { xs: 48, md: 52 }, pr: { xs: 1, sm: .5 }, fontSize: { xs: 12, md: 13 } },
              '& .MuiOutlinedInput-input': { py: 1.5 },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#7b807d' }} /></InputAdornment>,
              endAdornment: <InputAdornment position="end"><Button onClick={startInquiry} endIcon={<ArrowForward />} sx={{ bgcolor: '#087F5B', color: '#fff', borderRadius: '999px', px: { xs: 0.8, sm: 2 }, minWidth: { xs: 90, sm: 'auto' }, justifyContent: 'center', whiteSpace: 'nowrap', textTransform: 'none', fontSize: { xs: 11, sm: 13 }, '& .MuiButton-endIcon': { ml: { xs: 0.4, sm: 1 } }, '&:hover': { bgcolor: '#06694B' } }}>{tr('hero.getAQuote')}</Button></InputAdornment>,
            }}
          />
        </Box>

      </Box>

      </Box>

      {banners.length > 1 && (
        <Box className="buyer-hero-dots" sx={{ position: { xs: 'relative', md: 'absolute' }, alignSelf: { xs: 'flex-end', md: 'auto' }, mt: { xs: 1.5, md: 0 }, bottom: { md: 16 }, right: { md: 22 }, display: 'flex', gap: 0.75 }}>
          {banners.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIndex(i)}
              sx={{ width: i === index ? 20 : 8, height: 4, borderRadius: 2, bgcolor: i === index ? '#FFFFFF' : 'rgba(255,255,255,0.45)', cursor: 'pointer', transition: 'width .2s' }}
            />
          ))}
        </Box>
      )}
    </Box>
    <LoginPromptDialog open={loginPromptOpen && !isAuthenticated} onClose={() => setLoginPromptOpen(false)} from="/buyer/experience" returnState={prompt.trim() ? { initialPrompt: prompt.trim() } : undefined} />
    </>
  );
}
